/**
 * AgentStatusPanel Component (Issue #157)
 *
 * Container component that displays all agent statuses during analysis,
 * with overall progress summary and expandable agent cards.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { AgentStatusCard } from './AgentStatusCard';
import { api, type AgentRun } from '../../services/api';
import type { AnalysisJob } from '@veodiagram/shared';

interface AgentStatusPanelProps {
  jobId: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

export function AgentStatusPanel({ jobId, onComplete, onError }: AgentStatusPanelProps) {
  const [agents, setAgents] = useState<AgentRun[]>([]);
  const [jobStatus, setJobStatus] = useState<AnalysisJob | null>(null);
  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Calculate overall progress
  const overallProgress = agents.length > 0
    ? Math.round(agents.reduce((sum, a) => sum + a.progress, 0) / agents.length)
    : 0;

  // Count agents by status
  const statusCounts = agents.reduce((counts, agent) => {
    counts[agent.status] = (counts[agent.status] || 0) + 1;
    return counts;
  }, {} as Record<string, number>);

  // Check if all agents are done
  const allDone = agents.length > 0 && agents.every(a => a.status === 'complete' || a.status === 'error');
  const hasErrors = agents.some(a => a.status === 'error');

  // Fetch agent statuses and job status
  const fetchAgentStatus = useCallback(async () => {
    try {
      // Fetch both agent runs and job status in parallel
      const [agentRuns, job] = await Promise.all([
        api.getAgentRuns(jobId),
        api.getAnalysisStatus(jobId),
      ]);

      setAgents(agentRuns);
      setJobStatus(job);
      setIsLoading(false);

      // Issue #1073: agent runs are only created by the AI orchestrator. When
      // AI is disabled (no Anthropic key), agentRuns stays empty forever, so
      // the agent-runs-only check never fires and the parent's onComplete
      // handler never runs — leaving ProjectPage on a stale /diagrams snapshot
      // that includes partially-written diagrams but not yet the e2eFlow row,
      // which makes the "Generate E2E Flows" CTA stick until manual refresh.
      // Treat job-level terminal status as authoritative; the agent-runs check
      // remains as the fast path for AI-enabled runs.
      const agentRunsDone = agentRuns.length > 0 && agentRuns.every(
        a => a.status === 'complete' || a.status === 'error'
      );
      const jobDone = job.status === 'completed' || job.status === 'failed';
      const allComplete = agentRunsDone || jobDone;

      if (allComplete) {
        const hasAnyError = agentRuns.some(a => a.status === 'error') || job.status === 'failed';
        if (hasAnyError) {
          onError?.(job.error || 'Some agents failed during analysis');
        } else {
          onComplete?.();
        }
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error('Failed to fetch agent status:', error);
      // Don't stop polling on error, might be temporary
    }
  }, [jobId, onComplete, onError]);

  // Start polling on mount
  useEffect(() => {
    fetchAgentStatus();
    pollIntervalRef.current = setInterval(fetchAgentStatus, 1000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchAgentStatus]);

  // Toggle expanded agent
  const handleToggleExpand = (agentId: string) => {
    setExpandedAgentId(expandedAgentId === agentId ? null : agentId);
  };

  if (isLoading && agents.length === 0) {
    return (
      <div className="p-6 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/50">
        <div className="flex items-center justify-center gap-3">
          <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-slate-600 dark:text-slate-400">Initializing analysis agents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
      {/* Header with overall progress */}
      <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              Analysis Agents
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {agents.length} agents
            </span>
          </div>

          {/* Status Summary */}
          <div className="flex items-center gap-4">
            {statusCounts.complete && (
              <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <span>✅</span> {statusCounts.complete}
              </span>
            )}
            {(statusCounts.analyzing || statusCounts.generating) && (
              <span className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                <span>🔄</span> {(statusCounts.analyzing || 0) + (statusCounts.generating || 0)}
              </span>
            )}
            {statusCounts.idle && (
              <span className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                <span>⏳</span> {statusCounts.idle}
              </span>
            )}
            {statusCounts.error && (
              <span className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                <span>❌</span> {statusCounts.error}
              </span>
            )}
          </div>
        </div>

        {/* Current Pipeline Stage (Issue #373) */}
        {!allDone && jobStatus?.stage && (
          <div className="mt-3">
            {/* AI indicator for AI-related stages */}
            {jobStatus.stage.startsWith('AI:') ? (
              <div className="flex items-center gap-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-purple-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                      AI Processing
                    </span>
                    <span className="flex items-center gap-1 text-xs text-purple-500 dark:text-purple-400">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"/>
                      Communicating
                    </span>
                  </div>
                  <span className="text-sm text-purple-800 dark:text-purple-200">
                    {/* Parse and display clean stage name */}
                    {jobStatus.stage.replace(/^AI:\s*/, '')}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md">
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Processing
                </span>
                <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                  {jobStatus.stage}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Overall Progress Bar */}
        {!allDone && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-slate-600 dark:text-slate-400">Overall Progress</span>
              <span className="text-slate-800 dark:text-slate-200 font-medium">{Math.min(jobStatus?.progress ?? overallProgress, 99)}%</span>
            </div>
            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ease-out ${
                  hasErrors ? 'bg-yellow-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(jobStatus?.progress ?? overallProgress, 99)}%` }}
              />
            </div>
          </div>
        )}

        {/* Completion message */}
        {allDone && (
          <div className={`mt-3 p-2 rounded-lg text-sm ${
            hasErrors
              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
              : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
          }`}>
            {hasErrors
              ? '⚠️ Analysis completed with some errors. Some diagrams may not be available.'
              : '✅ All agents completed successfully!'}
          </div>
        )}
      </div>

      {/* Agent List */}
      <div className="p-4 space-y-3">
        {agents.map((agent) => (
          <AgentStatusCard
            key={agent.id}
            agent={agent}
            isExpanded={expandedAgentId === agent.id}
            onToggleExpand={() => handleToggleExpand(agent.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default AgentStatusPanel;
